import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {isEqual} from 'lodash';
import React, {useCallback, useMemo} from 'react';
import {toast} from 'react-toastify';
import {twMerge} from 'tailwind-merge';

import {trackAddPanels} from '../../services/analytics/addPanelsEvents';
import {trackPanelBankSection} from '../../services/analytics/panelBankSectionEvents';
import {useDispatch} from '../../state/hooks';
import {usePanelBankUpdaterContext} from '../../state/panelbank/context';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelBankConfigActions from '../../state/views/panelBankConfig/actions';
import * as PanelBankSectionConfigActions from '../../state/views/panelBankSectionConfig/actions';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {isMobile} from '../../util/panelbankFlow';
import {
  OverflowMenu,
  OverflowMenuCheckboxItem,
  OverflowMenuItem,
} from '../Views/components/OverflowMenu';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';

const MAX_CUSTOM_PANEL_COUNT = 50; // panel limit for custom layout (grid) sections

export const PanelBankSectionOverflowMenu = ({
  activePanelCount,
  isProjectReadOnly,
  panelBankSectionConfigRef,
  sectionLayoutType,
  sectionComponentRef,
  setIsEditingName,
}: {
  activePanelCount: number;
  isProjectReadOnly?: boolean;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  sectionLayoutType: 'grid' | 'flow';
  sectionComponentRef: React.RefObject<HTMLDivElement>;
  setIsEditingName: (isEditing: boolean) => void;
}) => {
  const {
    addPanelsClick,
    addSectionAbove,
    addSectionBelow,
    addSectionToReport,
    deleteSection,
    sortPanels,
    toggleLayoutType,
  } = usePanelBankSectionOverflowMenuActions(
    panelBankSectionConfigRef,
    sectionLayoutType,
    activePanelCount
  );

  if (isMobile()) {
    return null;
  }

  return (
    <OverflowMenu
      aria-label="Section actions"
      data-test="pb-section-overflow-menu-trigger"
      isRootModal={false} // modal=true interferes with input autofocus on "Rename section"
    >
      <OverflowMenuItem
        data-test="add-panels"
        displayName="Add panels"
        icon="add-new"
        onSelect={() => {
          addPanelsClick();
          sectionComponentRef.current?.scrollIntoView({
            block: 'start',
            inline: 'start',
            behavior: 'smooth',
          });
        }}
      />
      <DropdownMenu.Separator />
      <OverflowMenuItem
        icon="pencil-edit"
        displayName="Rename section"
        onSelect={() => setIsEditingName(true)}
      />
      <DropdownMenu.Separator />
      <OverflowMenuItem
        icon="sort"
        displayName="Sort panels A-Z"
        onSelect={sortPanels}
      />
      <DropdownMenu.Separator />
      <DropdownMenu.Label className="mb-8 mt-8 flex flex-wrap items-center justify-between gap-16 px-12">
        <label
          htmlFor="panelBankSectionLayout"
          className={twMerge(
            'flex gap-8 text-base font-semibold leading-[24px]',
            'text-moon-800 dark:text-moon-300'
          )}>
          Layout grid
        </label>
      </DropdownMenu.Label>
      <DropdownMenu.Group id="panelBankSectionLayout">
        <OverflowMenuCheckboxItem
          checked={sectionLayoutType === 'flow'}
          displayName="Standard grid"
          icon="layout-grid"
          onClick={toggleLayoutType}
        />
        <OverflowMenuCheckboxItem
          checked={sectionLayoutType === 'grid'}
          displayName="Custom grid"
          icon="select-move-tool"
          onClick={toggleLayoutType}
        />
      </DropdownMenu.Group>
      <DropdownMenu.Separator />
      <OverflowMenuItem
        icon="new-section-above"
        displayName="New section above"
        onSelect={addSectionAbove}
      />
      <OverflowMenuItem
        icon="new-section-below"
        displayName="New section below"
        onSelect={addSectionBelow}
      />
      {!isProjectReadOnly && (
        <>
          <DropdownMenu.Separator />
          <OverflowMenuItem
            icon="add-to-report"
            displayName="Add section to report"
            onSelect={addSectionToReport}
          />
        </>
      )}
      <DropdownMenu.Separator />
      <OverflowMenuItem
        data-test="delete-section"
        displayName="Delete section"
        icon="delete"
        onSelect={deleteSection}
      />
    </OverflowMenu>
  );
};

const usePanelBankSectionOverflowMenuActions = (
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref,
  sectionLayoutType: 'grid' | 'flow',
  activePanelCount: number
) => {
  const {addSectionAbove, addSectionBelow, addSectionToReport, deleteSection} =
    useSectionActions(panelBankSectionConfigRef);
  const {addPanelsClick, sortPanels} = useSectionPanelsActions(
    panelBankSectionConfigRef
  );
  const toggleLayoutType = useToggleSectionlayout(
    panelBankSectionConfigRef,
    sectionLayoutType,
    activePanelCount
  );

  return useMemo(
    () => ({
      addPanelsClick,
      addSectionAbove,
      addSectionBelow,
      addSectionToReport,
      deleteSection,
      sortPanels,
      toggleLayoutType,
    }),
    [
      addPanelsClick,
      addSectionAbove,
      addSectionBelow,
      addSectionToReport,
      deleteSection,
      sortPanels,
      toggleLayoutType,
    ]
  );
};

const useToggleSectionlayout = (
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref,
  sectionLayoutType: 'grid' | 'flow',
  activePanelCount: number
) => {
  const toggleType = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.toggleType
  );
  const toggleSectionLayout = useCallback(() => {
    // Flip the value because this is a toggle
    trackPanelBankSection({
      action: sectionLayoutType === 'grid' ? 'standard grid' : 'custom grid',
      numPanels: activePanelCount,
    });

    if (
      sectionLayoutType !== 'grid' &&
      activePanelCount > MAX_CUSTOM_PANEL_COUNT
    ) {
      toast(`Custom Layout is limited to ${MAX_CUSTOM_PANEL_COUNT} panels.`);
    } else {
      toggleType();
    }
  }, [sectionLayoutType, activePanelCount, toggleType]);

  return toggleSectionLayout;
};

const useSectionActions = (
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) => {
  const {panelBankConfigRef, workspaceSettingsRef} = useWorkspaceRefsContext();
  const {setExportingSectionRefID} = usePanelBankUpdaterContext();
  const {targetSectionRef: workspaceDrawerTarget, closeWorkspaceDrawer} =
    useWorkspaceDrawerContext();

  const addSectionToReport = useCallback(() => {
    setExportingSectionRefID(panelBankSectionConfigRef.id);
  }, [setExportingSectionRefID, panelBankSectionConfigRef]);

  // adding section above or below
  const addSectionAction = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.addSection
  );
  const addSectionAbove = useCallback(() => {
    addSectionAction(panelBankSectionConfigRef);
  }, [addSectionAction, panelBankSectionConfigRef]);
  const addSectionBelow = useCallback(() => {
    addSectionAction(panelBankSectionConfigRef, {addAfter: true});
  }, [addSectionAction, panelBankSectionConfigRef]);

  // delete section
  const deleteSectionAction = ViewHooks.useViewActionBindAll(
    PanelBankConfigActions.deleteSection,
    panelBankConfigRef,
    panelBankSectionConfigRef,
    workspaceSettingsRef
  );
  const deleteSection = useCallback(() => {
    if (isEqual(panelBankSectionConfigRef, workspaceDrawerTarget)) {
      closeWorkspaceDrawer();
    }
    deleteSectionAction();
  }, [
    closeWorkspaceDrawer,
    deleteSectionAction,
    panelBankSectionConfigRef,
    workspaceDrawerTarget,
  ]);

  return useMemo(
    () => ({
      addSectionAbove,
      addSectionBelow,
      addSectionToReport,
      deleteSection,
    }),
    [addSectionAbove, addSectionBelow, addSectionToReport, deleteSection]
  );
};

const useSectionPanelsActions = (
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) => {
  const dispatch = useDispatch();
  const {openWorkspaceDrawer} = useWorkspaceDrawerContext();

  const addPanelsClick = useCallback(() => {
    openWorkspaceDrawer('add-panels', panelBankSectionConfigRef);
    trackAddPanels({
      action: 'Drawer opened',
      scope: 'section',
    });
  }, [openWorkspaceDrawer, panelBankSectionConfigRef]);

  const sortPanels = useCallback(() => {
    return dispatch(
      PanelBankSectionConfigActions.sortPanels([panelBankSectionConfigRef])
    );
  }, [dispatch, panelBankSectionConfigRef]);

  return useMemo(
    () => ({
      addPanelsClick,
      sortPanels,
    }),
    [addPanelsClick, sortPanels]
  );
};
